import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import classNames from "classnames";
import { Col, Row, Table } from "react-bootstrap";

import GameActionButtons from "./ActionButtons";
import { useMyScheduleContext } from "../contexts/MySchedule";
import { dateFormat } from "../lib/dateFormat";
import { SortColumns } from "../types/myschedule";

import { usePaginationContext } from "../../../../contexts/Pagination";
import { useUmpireInfoModalContext } from "../../../../contexts/UmpireInfoModal";
import { selectUser } from "../../../../features/auth/authSlice";
import { selectAllSites } from "../../../../features/global/globalSlice";
import { useAppSelector } from "../../../../hooks/reduxHooks";
import { defaultTimeFormat } from "../../../../lib/utils";
import { IGameTeam, IUmpire } from "../../../../types/api";
import { UmpirePay } from "../../../../types/shared";
import { useCoachInfoModalContext } from "../contexts/CoachInfoModal";

const GamesTable = React.memo(() => {
    const {
        games,
        schedule,
        sortColumn,
        sortOrder,
        setSortColumn,
        setSortOrder,
        umpirePay,
    } = useMyScheduleContext();
    const { page, itemsPerPage } = usePaginationContext();
    const { setTeam, setShowCoachInfoModal } = useCoachInfoModalContext();
    const { setShowUmpireInfoModal, setUmpireInfoModalContent } =
        useUmpireInfoModalContext();
    const sites = useAppSelector(selectAllSites);
    const user = useAppSelector(selectUser);

    const paginatedGames = useMemo(() => {
        return games.slice(
            page * itemsPerPage - itemsPerPage,
            page * itemsPerPage,
        );
    }, [games, page, itemsPerPage]);

    const handleHeaderClick = (column: SortColumns) => {
        if (column !== sortColumn) {
            setSortColumn(column);
            setSortOrder("ascending");
        } else {
            setSortOrder(prev =>
                prev === "ascending" ? "descending" : "ascending",
            );
        }
    };

    const handleUmpireClick = (otherUmpire: IUmpire | undefined) => {
        setUmpireInfoModalContent(otherUmpire);
        setShowUmpireInfoModal(true);
    };

    const handleCoachClick = (team: IGameTeam) => {
        setTeam(team);
        setShowCoachInfoModal(true);
    };

    return (
        <Row>
            <Col>
                <Table
                    striped
                    className={classNames("myScheduleTable", "mt-2")}
                >
                    <thead>
                        <tr>
                            <th
                                onClick={() => handleHeaderClick("date")}
                                className={classNames({
                                    sorting_asc:
                                        sortColumn === "date" &&
                                        sortOrder === "ascending",
                                    sorting_desc:
                                        sortColumn === "date" &&
                                        sortOrder === "descending",
                                })}
                            >
                                Date
                            </th>
                            <th
                                onClick={() => handleHeaderClick("location")}
                                className={classNames({
                                    sorting_asc:
                                        sortColumn === "location" &&
                                        sortOrder === "ascending",
                                    sorting_desc:
                                        sortColumn === "location" &&
                                        sortOrder === "descending",
                                })}
                            >
                                Field
                            </th>
                            <th
                                onClick={() => handleHeaderClick("site")}
                                className={classNames({
                                    sorting_asc:
                                        sortColumn === "site" &&
                                        sortOrder === "ascending",
                                    sorting_desc:
                                        sortColumn === "site" &&
                                        sortOrder === "descending",
                                })}
                            >
                                Site
                            </th>
                            <th
                                onClick={() => handleHeaderClick("age")}
                                className={classNames({
                                    sorting_asc:
                                        sortColumn === "age" &&
                                        sortOrder === "ascending",
                                    sorting_desc:
                                        sortColumn === "age" &&
                                        sortOrder === "descending",
                                })}
                            >
                                Age
                            </th>
                            <th data-sortable="false">Game Details</th>
                            <th data-sortable="false">Pay</th>
                            <th data-sortable="false"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.keys(umpirePay).length !== 0 &&
                            paginatedGames.map((g, idx) => {
                                let otherUmpire: IUmpire | undefined =
                                    undefined;
                                let otherUmpirePay:
                                    | UmpirePay["umpires"][number]
                                    | undefined = undefined;
                                const site = sites.find(s => s.id === g.site);
                                const sitePay = umpirePay[g.site];
                                const ageLevelPay = sitePay.find(
                                    p => p.ageLevel === g.ageLevel.ageLevel,
                                );
                                const gameUmpire = g.umpires?.find(
                                    u => u.id === user?.id,
                                );
                                const gameUmpirePay = ageLevelPay?.umpires.find(
                                    u => {
                                        return gameUmpire?.location === "field"
                                            ? u.title === "Field Umpire"
                                            : u.title !== "Field Umpire";
                                    },
                                );

                                if (
                                    g.umpires &&
                                    g.umpires.filter(
                                        g => g.pendingReplacement !== 1,
                                    ).length > 1
                                ) {
                                    otherUmpire = g.umpires.find(
                                        u =>
                                            u.location !== gameUmpire?.location,
                                    );
                                    otherUmpirePay = ageLevelPay?.umpires.find(
                                        u => {
                                            return otherUmpire?.location ===
                                                "field"
                                                ? u.title === "Field Umpire"
                                                : u.title !== "Field Umpire";
                                        },
                                    );
                                }

                                return (
                                    <tr key={g.id || `placeholder-${idx}`}>
                                        <td style={{ width: "12%" }}>
                                            {dateFormat.format(
                                                new Date(g.date),
                                            )}
                                            <br />@{" "}
                                            {defaultTimeFormat.format(
                                                new Date(g.date),
                                            )}
                                        </td>
                                        <td style={{ width: "17%" }}>
                                            <Link
                                                to={
                                                    site
                                                        ? `https://www.${site.site}/fields/${g.fieldId}`
                                                        : `https://www.majorybl.com/fields/${g.fieldId}`
                                                }
                                                target="_blank"
                                            >
                                                {g.fieldName}
                                            </Link>
                                        </td>
                                        <td>
                                            {
                                                sites.find(s => s.id === g.site)
                                                    ?.displayName
                                            }
                                        </td>
                                        <td style={{ width: "6%" }}>
                                            {g.ageLevel.ageLevel}
                                        </td>
                                        <td style={{ width: "22%" }}>
                                            <b>{g.leagueName}</b>
                                            {g.site === 1 ? (
                                                <>
                                                    <br />
                                                    {g.visitor.name ? (
                                                        <span
                                                            className={classNames(
                                                                "coachInfo",
                                                            )}
                                                            onClick={() =>
                                                                handleCoachClick(
                                                                    g.visitor,
                                                                )
                                                            }
                                                        >
                                                            {g.visitor.name}
                                                        </span>
                                                    ) : (
                                                        "TBD"
                                                    )}{" "}
                                                    @{" "}
                                                    {g.home.name ? (
                                                        <span
                                                            className={classNames(
                                                                "coachInfo",
                                                            )}
                                                            onClick={() =>
                                                                handleCoachClick(
                                                                    g.home,
                                                                )
                                                            }
                                                        >
                                                            {g.home.name}
                                                        </span>
                                                    ) : (
                                                        "TBD"
                                                    )}
                                                </>
                                            ) : (
                                                ""
                                            )}
                                        </td>
                                        <td style={{ width: "20%" }}>
                                            {gameUmpire?.location ===
                                            "plate" ? (
                                                g.umpires &&
                                                g.umpires.filter(
                                                    g =>
                                                        g.pendingReplacement !==
                                                        1,
                                                ).length > 1 ? (
                                                    <>
                                                        {g.site === 1
                                                            ? gameUmpirePay?.title
                                                            : schedule ===
                                                                "current"
                                                              ? `Umpire 1`
                                                              : gameUmpirePay?.title}
                                                        : ${gameUmpirePay?.pay}
                                                        <br />
                                                        {
                                                            otherUmpirePay?.title
                                                        }:{" "}
                                                        <span
                                                            className={classNames(
                                                                "otherUmpireLink",
                                                            )}
                                                            onClick={() =>
                                                                handleUmpireClick(
                                                                    otherUmpire,
                                                                )
                                                            }
                                                        >
                                                            {otherUmpire?.name}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        {g.site === 1
                                                            ? gameUmpirePay?.title
                                                            : schedule ===
                                                                "current"
                                                              ? `Umpire 1`
                                                              : gameUmpirePay?.title}
                                                        : ${gameUmpirePay?.pay}
                                                    </>
                                                )
                                            ) : g.umpires &&
                                              g.umpires.filter(
                                                  g =>
                                                      g.pendingReplacement !==
                                                      1,
                                              ).length > 1 ? (
                                                <>
                                                    {otherUmpirePay?.title}:{" "}
                                                    <span
                                                        className={classNames(
                                                            "otherUmpireLink",
                                                        )}
                                                        onClick={() =>
                                                            handleUmpireClick(
                                                                otherUmpire,
                                                            )
                                                        }
                                                    >
                                                        {otherUmpire?.name}
                                                    </span>
                                                    <br />
                                                    {g.site === 1
                                                        ? gameUmpirePay?.title
                                                        : schedule === "current"
                                                          ? `Umpire 2`
                                                          : gameUmpirePay?.title}
                                                    : ${gameUmpirePay?.pay}
                                                </>
                                            ) : (
                                                <>
                                                    {g.site === 1
                                                        ? gameUmpirePay?.title
                                                        : schedule === "current"
                                                          ? `Umpire 2`
                                                          : gameUmpirePay?.title}
                                                    : ${gameUmpirePay?.pay}
                                                </>
                                            )}
                                        </td>
                                        <td
                                            style={{ width: "16%" }}
                                            className={classNames("text-right")}
                                        >
                                            <GameActionButtons game={g} />
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                    <tfoot>
                        <tr>
                            <th>Date</th>
                            <th>Field</th>
                            <th>Site</th>
                            <th>Age</th>
                            <th>Game Details</th>
                            <th>Pay</th>
                            <th>&nbsp;</th>
                        </tr>
                    </tfoot>
                </Table>
            </Col>
        </Row>
    );
});

export default GamesTable;
